import {
  ChangeEvent,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useEffect,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import { UPLOAD_INPUT_TYPES } from '__components/Kizen/FileInputWithImage';
import {
  StyledFilePicker,
  StyledSmartConnectorName,
  StyledResponseError,
  StyledSwitch,
  StyledTestExplanation,
  StyledUTF8BOMWarning,
  StyledFileName,
} from './styles';
import { IconWithTooltip } from '__pages/Common/components/LinkWithIconTooltip/IconWithTooltip';
import { ALLOWED_SOURCE_FILE_EXTENSIONS } from '../../constants';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import { SmartConnector } from 'pages/SmartConnectors/types';
import { useUTF8BOMWarning } from 'pages/SmartConnectors/hooks/useUTF8BOMWarning';

export type RunSmartConnectorModalProps = {
  show: boolean;
  skipDiffCheck: boolean;
  setSkipDiffCheck: Dispatch<SetStateAction<boolean>>;
  canSkipDiffCheck: boolean;
  onConfirm: () => Promise<void>;
  onHide: () => void;
  file: File | null;
  setFile: Dispatch<SetStateAction<File | null>>;
  isDryRun: boolean;
  smartConnector: SmartConnector | null;
  showTimeoutWarning: boolean;
  persistentErrorMessage?: string;
  isReprocess?: boolean;
};

const Ellipsis: FC<PropsWithChildren> = ({ children }) => (
  <StyledSmartConnectorName>
    <TextEllipsisWithTooltip as="span">{children}</TextEllipsisWithTooltip>
  </StyledSmartConnectorName>
);

export const RunSmartConnectorModal = ({
  isDryRun = false,
  file,
  setFile,
  skipDiffCheck,
  setSkipDiffCheck,
  canSkipDiffCheck,
  smartConnector,
  showTimeoutWarning,
  persistentErrorMessage,
  isReprocess,
  ...modalProps
}: RunSmartConnectorModalProps) => {
  const { t } = useTranslation();

  const [showUTF8Warning, checkUTF8Warning, resetUTF8Warning] =
    useUTF8BOMWarning();

  const onChange = (files: File[]) => {
    if (files.length) {
      setFile(files[0]);
    }
    return null;
  };

  useEffect(() => {
    if (file) {
      checkUTF8Warning(file);
    } else {
      resetUTF8Warning();
    }
  }, [file, checkUTF8Warning, resetUTF8Warning]);

  const onRemove = () => {
    setFile(null);
    resetUTF8Warning();
  };

  const handleChangeDiffChecking = (ev: ChangeEvent<HTMLInputElement>) =>
    setSkipDiffCheck(ev.target.checked);

  return (
    <ConfirmationModal
      heading={
        isReprocess
          ? isDryRun
            ? t('Reprocess File (Dry Run)')
            : t('Reprocess File (Live)')
          : isDryRun
            ? t('Start Dry Run')
            : t('Process File')
      }
      buttonText={t('Start')}
      disabled={((!isDryRun || isReprocess) && !file) || showTimeoutWarning}
      {...modalProps}
    >
      <StyledTestExplanation>
        {isDryRun ? (
          <Trans
            i18nKey="This will execute all the steps in your <ellipsis>{{name}}</ellipsis> SmartConnector and generate a workbook of changes and errors, if any. No changes will be uploaded to your data in Kizen."
            values={{
              name: smartConnector?.name,
            }}
            components={{
              ellipsis: <Ellipsis />,
            }}
          />
        ) : (
          <Trans
            i18nKey="This will execute all the steps in your <ellipsis>{{name}}</ellipsis> SmartConnector and insert or update <strong>live data</strong> in Kizen."
            values={{ name: smartConnector?.name }}
            components={{
              ellipsis: <Ellipsis />,
            }}
          />
        )}
      </StyledTestExplanation>
      {isReprocess ? (
        <StyledFileName
          label={t('Selected File')}
          value={file?.name}
          disabled
        />
      ) : (
        <StyledFilePicker
          label={
            <>
              {isDryRun ? (
                <>
                  {t('Upload test file')}{' '}
                  <IconWithTooltip
                    label={t(
                      'Will use the SmartConnector input file reference if not provided.'
                    )}
                  />
                </>
              ) : (
                t('Upload file')
              )}
            </>
          }
          onChange={onChange}
          onRemove={onRemove}
          file={file}
          showImage={false}
          type={UPLOAD_INPUT_TYPES.AVATAR}
          acceptedExtensions={ALLOWED_SOURCE_FILE_EXTENSIONS.toString()}
          id="upload-test-file"
        />
      )}
      {showUTF8Warning ? (
        <StyledUTF8BOMWarning>
          {t(
            'A possible non-UTF-8 file was detected. Proceed with caution, as this may cause processing issues. For best results, ensure the file is UTF-8 encoded'
          )}
        </StyledUTF8BOMWarning>
      ) : null}
      {canSkipDiffCheck ? (
        <StyledSwitch
          onChange={handleChangeDiffChecking}
          checked={skipDiffCheck}
          removeMargin
          label={t('Skip Diff Check and Process All Rows')}
          textPlacement="regular-top"
        />
      ) : null}
      {showTimeoutWarning ? (
        <StyledResponseError>
          {t('Possible error starting job{{message}}', {
            message: `${persistentErrorMessage ? `: ${persistentErrorMessage}` : ''}`,
          })}
          <br />
          <br />
          {t(
            'Please wait 5 minutes and check the execution progress to see if it was actually started before re-uploading.'
          )}
        </StyledResponseError>
      ) : null}
    </ConfirmationModal>
  );
};
